import {useForm} from '@tanstack/react-form';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {NavLink, useNavigate, useParams} from 'react-router-dom';
import {Breadcrumb} from '~/components/Breadcrumps';
import {BreadcrumbsResourcePage, ResourceBlock} from '~/components/ResourcePage';
import {Button} from '~/components/Button';
import {TableSort, TableSortDirection} from '~/components/Table';
import {Title} from '~/components/Title';
import {Input} from '~/components/Controls/Input';
import {SelectInput} from '~/components/Controls/Select';
import {CheckboxField, InputField, MultiSelectField} from '~/components/Form';
import {InfoPopover} from '~/components/InfoPopover';
import {useAbility} from '~/lib/ability';
import {useModals} from '~/components/Modal';
import {useConfirm} from '~/components/Dialogs';
import {Intent} from '~/lib/intent';
import {sortByNumber, sortByString} from '~/lib/sort';
import {renderBoolean} from '~/lib/helpers';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {Progress} from '~/components/Progress';
import {notify} from '~/lib/notify';
import {clientsLinks} from '~/modules/Clients';
import {biddersLinks} from '~/modules/Bidders';
import {BiddersAbility} from '~/modules/Bidders/ability';
import {createBidderGroup, fetchBidder, fetchBiddersLite, pauseBidderGroupBidders, unpauseBidderGroupBidders, updateBidder, updateBidderGroup, prepareBidderSubmitData} from '~/modules/Bidders/api';
import {BidderForm, IBidderForm} from '~/modules/Bidders/forms/BidderForm';
import {BidderFormCtx, Field, setNestedValue} from '~/modules/Bidders/forms/BidderFormBridge';
import {bidderGroupFormSchema, toBidderGroupFormValues, toBidderGroupSubmitValues} from '~/modules/Bidders/models/BidderGroupForm';
import {IBulkPauseResult, IVT_SENSITIVE_HELP_TEXT} from '~/modules/Bidders/models/BidderGroup';
import {bidderStatusTags, IBidderLite} from '~/modules/Bidders/models/BidderLite';
import {BIDDER_GROUP_QUERY_KEY, useBidderGroupQuery} from '~/modules/Bidders/queries';
import {TagsList} from '~/components/Controls/Tags';

const STATUS_FILTER_ITEMS = [
    {label: 'Enabled', value: 'enabled'},
    {label: 'Paused', value: 'paused'},
    {label: 'Disabled', value: 'disabled'},
];

interface IParams extends Record<string, string | undefined> {
    groupId?: string;
}

const useBiddersLite = () => useQuery<IBidderLite[]>({
    queryKey: ['bidders', 'lite'],
    queryFn: () => fetchBiddersLite(),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
});

const useBidderItems = () => {
    const biddersQuery = useBiddersLite();
    const items = useMemo(() =>
        sortByString((biddersQuery.data ?? []).map(bidder => ({
            prefix: `#${bidder.id}`,
            label: bidder.name,
            value: bidder.id,
        })), 'label', TableSortDirection.ASC),
        [biddersQuery.data]
    );
    return {items, bidders: biddersQuery.data ?? [], isLoading: biddersQuery.isLoading};
};

const describeBulkPauseResult = (action: 'Paused' | 'Unpaused', result: IBulkPauseResult): string => {
    const parts = [`${action} ${result.affected.length} bidder(s)`];
    if (result.skipped.length > 0) parts.push(`skipped ${result.skipped.length}`);
    if (result.notFound.length > 0) parts.push(`${result.notFound.length} not found`);
    return parts.join(', ');
};

const BidderGroupCreateView = () => {
    const navigate = useNavigate();
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, any>>({});
    const {items: bidderItems, bidders: allBidders, isLoading: biddersLoading} = useBidderItems();

    const form = useForm({
        defaultValues: toBidderGroupFormValues(),
        validators: {
            onSubmit: ({value}) => {
                const result = bidderGroupFormSchema.safeParse(value);
                if (result.success) {
                    setFieldErrors({});
                    return undefined;
                }
                const newErrors: Record<string, any> = {};
                result.error.issues.forEach((issue: {path: (string | number)[]; message: string}) => {
                    setNestedValue(newErrors, issue.path.join('.'), issue.message);
                });
                setFieldErrors(newErrors);
                return 'Validation failed';
            }
        },
        onSubmit: async ({value}) => {
            try {
                const group = toBidderGroupSubmitValues(value);
                group.bidders = (group.bidderIds ?? [])
                    .map(id => allBidders.find(b => b.id === id))
                    .filter((b): b is IBidderLite => Boolean(b));
                const created = await createBidderGroup(group);
                notify.success('Saved');
                const id = typeof created?.id === 'number' ? created.id : null;
                navigate(id != null ? biddersLinks.bidderGroup(id) : biddersLinks.bidderGroups());
            } catch (error) {
                notify.apiError(error);
            }
        }
    });

    return (
        <Breadcrumb breadcrumb={{to: biddersLinks.bidderGroups(), title: 'Bidder groups'}}>
            <form.Subscribe selector={state => state}>
                {state => {
                    const setFieldValue = (fieldName: string, fieldValue: unknown) => {
                        form.setFieldValue(fieldName as never, fieldValue as never);
                    };
                    const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
                        event?.preventDefault();
                        void form.handleSubmit();
                    };
                    const formBridge = {
                        errors: fieldErrors,
                        touched: {},
                        submitCount: state.submissionAttempts,
                        setFieldValue,
                        setStatus: (_: unknown) => undefined,
                        setSubmitting: (_: boolean) => undefined,
                    };
                    const isSubmitting = state.isSubmitting;

                    return (
                        <BidderFormCtx.Provider value={{form, formBridge}}>
                            <BreadcrumbsResourcePage
                                title="Create bidder group"
                                toolbar={<>
                                    <Button
                                        intent={Intent.PRIMARY}
                                        responsive
                                        disabled={isSubmitting}
                                        leftIcon={<i className="fa fa-check"/>}
                                        onClick={() => void form.handleSubmit()}
                                    >Create</Button>
                                    <Button
                                        responsive
                                        disabled={isSubmitting}
                                        leftIcon={<i className="fa fa-times"/>}
                                        onClick={() => navigate(biddersLinks.bidderGroups())}
                                    >Cancel</Button>
                                </>}
                            >
                                <form onSubmit={handleSubmit}>
                                    <ResourceBlock title="General">
                                        <Field
                                            name="name"
                                            label="Name"
                                            options={{placeholder: 'Name', disabled: isSubmitting, autoFocus: true}}
                                            component={InputField}
                                        />
                                        <Field
                                            name="bidderIds"
                                            label="Bidders"
                                            options={{
                                                placeholder: 'Select bidders',
                                                searchable: true,
                                                clearable: true,
                                                items: bidderItems,
                                                disabled: isSubmitting || biddersLoading,
                                                rightIcon: biddersLoading && <i className="fa fa-spinner fa-spin"/>,
                                                columns: true,
                                            }}
                                            component={MultiSelectField}
                                        />
                                        <div style={{marginTop: '12px', display: 'flex', alignItems: 'center', gap: '4px'}}>
                                            <Field
                                                name="ivtSensitive"
                                                label="IVT sensitive"
                                                options={{disabled: isSubmitting}}
                                                component={CheckboxField}
                                            />
                                            <InfoPopover content={IVT_SENSITIVE_HELP_TEXT}/>
                                        </div>
                                    </ResourceBlock>
                                </form>
                            </BreadcrumbsResourcePage>
                        </BidderFormCtx.Provider>
                    );
                }}
            </form.Subscribe>
        </Breadcrumb>
    );
};

const BidderGroupDetailsView = () => {
    const {groupId} = useParams<IParams>();
    const numericGroupId = Number(groupId);
    const ability = useAbility(BiddersAbility);
    const navigate = useNavigate();
    const modals = useModals();
    const confirm = useConfirm();
    const queryClient = useQueryClient();
    const groupQuery = useBidderGroupQuery(numericGroupId);
    const [filter, setFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [pendingBidderId, setPendingBidderId] = useState<number | null>(null);
    const [editing, setEditing] = useState(false);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, any>>({});
    const {items: bidderItems, bidders: allBidders, isLoading: biddersLoading} = useBidderItems();

    const form = useForm({
        defaultValues: toBidderGroupFormValues(groupQuery.data),
        validators: {
            onSubmit: ({value}) => {
                const result = bidderGroupFormSchema.safeParse(value);
                if (result.success) {
                    setFieldErrors({});
                    return undefined;
                }
                const newErrors: Record<string, any> = {};
                result.error.issues.forEach((issue: {path: (string | number)[]; message: string}) => {
                    setNestedValue(newErrors, issue.path.join('.'), issue.message);
                });
                setFieldErrors(newErrors);
                return 'Validation failed';
            }
        },
        onSubmit: async ({value}) => {
            const group = groupQuery.data;
            if (!group) return;
            try {
                const submitGroup = toBidderGroupSubmitValues(value, group);
                submitGroup.bidders = (submitGroup.bidderIds ?? [])
                    .map(id => allBidders.find(b => b.id === id))
                    .filter((b): b is IBidderLite => Boolean(b));
                await updateBidderGroup(submitGroup);
                queryClient.invalidateQueries({queryKey: BIDDER_GROUP_QUERY_KEY(numericGroupId)});
                setEditing(false);
                notify.success('Saved');
            } catch (error) {
                notify.apiError(error);
            }
        }
    });

    const hasSyncedGroupDefaults = useRef(false);
    useEffect(() => {
        if (groupQuery.data && !hasSyncedGroupDefaults.current) {
            hasSyncedGroupDefaults.current = true;
            form.reset(toBidderGroupFormValues(groupQuery.data));
        }
    }, [groupQuery.data]);

    const editBidderModal = modals.create<IBidderForm>(BidderForm, {
        onSubmit: (bidder, formActions, close) => {
            updateBidder(prepareBidderSubmitData(bidder)).then(() => {
                close();
                queryClient.invalidateQueries({queryKey: BIDDER_GROUP_QUERY_KEY(numericGroupId)});
            }).catch(error => {
                formActions.setSubmitting(false);
                formActions.setStatus(getApiErrorMessage(error));
            });
        }
    });

    const filteredBidders = useMemo(() => {
        let bidders = groupQuery.data?.bidders ?? [];
        if (filter.trim()) {
            const normalized = filter.trim().toLowerCase();
            bidders = bidders.filter(bidder => bidder.name.toLowerCase().includes(normalized) ||
                String(bidder.id).includes(normalized));
        }
        if (statusFilter) {
            bidders = bidders.filter(bidder => bidder.status === statusFilter);
        }
        return bidders;
    }, [groupQuery.data?.bidders, filter, statusFilter]);

    const handleEditBidder = (bidderId: number) => {
        setPendingBidderId(bidderId);
        fetchBidder(bidderId).then(bidder => {
            editBidderModal.show({bidder});
        }).catch((error) => {
            notify.apiError(error, 'Failed to load bidder details');
        }).finally(() => setPendingBidderId(prev => prev === bidderId ? null : prev));
    };

    const pauseMutation = useMutation({mutationFn: (id: number) => pauseBidderGroupBidders(id)});
    const unpauseMutation = useMutation({mutationFn: (id: number) => unpauseBidderGroupBidders(id)});

    const hasIvtPausedBidder = useMemo(
        () => (groupQuery.data?.bidders ?? []).some(b => b.status === 'paused' && b.pauseReasons?.includes('ivt_protection')),
        [groupQuery.data?.bidders]
    );

    const handleTogglePauseAll = (targetGroup: {id: number; name: string; bidders?: IBidderLite[]}) => {
        const message = hasIvtPausedBidder
            ? `Unpause the bidders in "${targetGroup.name}" that are paused for IVT protection? ` +
              'Bidders paused for another reason are left alone.'
            : `Pause all ${targetGroup.bidders?.length ?? 0} bidders in "${targetGroup.name}"? ` +
              'Demand from these bidders stops until someone unpauses them.';
        const mutation = hasIvtPausedBidder ? unpauseMutation : pauseMutation;
        const verb = hasIvtPausedBidder ? 'Unpaused' : 'Paused';
        const errorText = hasIvtPausedBidder ? 'Failed to unpause bidders' : 'Failed to pause bidders';

        confirm(message, {
            icon: <i className='fa fa-question'/>,
            intent: Intent.DANGER,
            onOk: () => {
                mutation.mutate(targetGroup.id, {
                    onSuccess: (result) => {
                        notify.success(describeBulkPauseResult(verb, result));
                        groupQuery.refetch();
                    },
                    onError: (error) => notify.apiError(error, errorText),
                });
            }
        });
    };

    if (groupQuery.isPending) {
        return <Progress message="Loading bidder group"/>;
    }

    const group = groupQuery.data;

    if (!group) {
        return (
            <Breadcrumb breadcrumb={{to: biddersLinks.bidderGroups(), title: 'Bidder groups'}}>
                <BreadcrumbsResourcePage title='Bidder groups'>
                    <Title>Bidder group not found</Title>
                    <Button onClick={() => navigate(biddersLinks.bidderGroups())}>Back to groups</Button>
                </BreadcrumbsResourcePage>
            </Breadcrumb>
        );
    }

    return <Breadcrumb breadcrumb={{to: biddersLinks.bidderGroups(), title: group.name}}>
        <form.Subscribe selector={state => state}>
            {state => {
                const setFieldValue = (fieldName: string, fieldValue: unknown) => {
                    form.setFieldValue(fieldName as never, fieldValue as never);
                };
                const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
                    event?.preventDefault();
                    void form.handleSubmit();
                };
                const formBridge = {
                    errors: fieldErrors,
                    touched: {},
                    submitCount: state.submissionAttempts,
                    setFieldValue,
                    setStatus: (_: unknown) => undefined,
                    setSubmitting: (_: boolean) => undefined,
                };
                const isSubmitting = state.isSubmitting;

                return (
                    <BidderFormCtx.Provider value={{form, formBridge}}>
                        <BreadcrumbsResourcePage title={group.name} toolbar={
                            ability.canEdit() && (
                                editing ? (<>
                                    <Button
                                        responsive
                                        intent={Intent.PRIMARY}
                                        disabled={isSubmitting}
                                        leftIcon={<i className="fa fa-check"/>}
                                        onClick={() => void form.handleSubmit()}
                                    >Save</Button>
                                    <Button
                                        responsive
                                        disabled={isSubmitting}
                                        leftIcon={<i className="fa fa-times"/>}
                                        onClick={() => { setEditing(false); form.reset(); }}
                                    >Cancel</Button>
                                </>) : (<>
                                    <Button
                                        responsive
                                        leftIcon={<i className="fa fa-edit"/>}
                                        disabled={groupQuery.isPending}
                                        onClick={() => setEditing(true)}
                                    >Edit</Button>
                                </>)
                            )
                        }>
                            <form onSubmit={handleSubmit}>
                                <ResourceBlock title="General">
                                    {editing ? (<>
                                        <Field
                                            name="name"
                                            label="Name"
                                            options={{placeholder: 'Name', disabled: isSubmitting}}
                                            component={InputField}
                                        />
                                        <Field
                                            name="bidderIds"
                                            label="Bidders"
                                            options={{
                                                placeholder: 'Select bidders',
                                                searchable: true,
                                                clearable: true,
                                                items: bidderItems,
                                                disabled: isSubmitting || biddersLoading,
                                                rightIcon: biddersLoading && <i className="fa fa-spinner fa-spin"/>,
                                                columns: true,
                                            }}
                                            component={MultiSelectField}
                                        />
                                        <div style={{marginTop: '12px', display: 'flex', alignItems: 'center', gap: '4px'}}>
                                            <Field
                                                name="ivtSensitive"
                                                label="IVT sensitive"
                                                options={{disabled: isSubmitting}}
                                                component={CheckboxField}
                                            />
                                            <InfoPopover content={IVT_SENSITIVE_HELP_TEXT}/>
                                        </div>
                                    </>) : (<>
                                        <div style={{display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', alignItems: 'center'}}>
                                            <span style={{color: 'var(--text-secondary)', fontSize: '13px'}}>ID</span>
                                            <span>{group.id}</span>
                                            <span style={{color: 'var(--text-secondary)', fontSize: '13px'}}>Name</span>
                                            <span>{group.name}</span>
                                            <span style={{color: 'var(--text-secondary)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px'}}>
                                                IVT sensitive <InfoPopover content={IVT_SENSITIVE_HELP_TEXT}/>
                                            </span>
                                            <span>{renderBoolean(group.ivtSensitive)}</span>
                                        </div>
                                    </>)}
                                </ResourceBlock>
                            </form>
                            <div className="layout-content-box">
                                <Title toolbar={ability.canEdit() && group.ivtSensitive && (
                                    <Button
                                        responsive
                                        leftIcon={<i className={`fa ${hasIvtPausedBidder ? 'fa-play' : 'fa-pause'}`}/>}
                                        disabled={pauseMutation.isPending || unpauseMutation.isPending}
                                        rightIcon={(pauseMutation.isPending || unpauseMutation.isPending) &&
                                            <i className="fa fa-spinner fa-spin"/>}
                                        onClick={() => handleTogglePauseAll(group)}
                                    >{hasIvtPausedBidder ? 'Unpause all' : 'Pause all'}</Button>
                                )}>Bidders</Title>
                                <form style={{display: 'flex', gap: '10px', marginBottom: '10px'}} onSubmit={e => e.preventDefault()}>
                                    <Input
                                        value={filter}
                                        onChange={setFilter}
                                        placeholder='Filter by name or ID'
                                        rightIcon={<i className='fa fa-search input-search-icon'/>}
                                    />
                                    <SelectInput<string | null>
                                        items={STATUS_FILTER_ITEMS}
                                        value={statusFilter}
                                        onChange={setStatusFilter}
                                        placeholder='Status'
                                        clearable
                                    />
                                </form>
                                <TableSort records={filteredBidders}
                                    header
                                    defaultSort={{sort: sortByNumber, field: 'id', direction: TableSortDirection.DESC}}
                                    columns={[{
                                        header: 'ID',
                                        field: 'id',
                                        sort: sortByNumber,
                                        small: true
                                    }, {
                                        header: 'Name',
                                        field: 'name',
                                        sort: sortByString,
                                        cell: bidder => <NavLink to={biddersLinks.bidder(bidder.id)}>{bidder.name}</NavLink>
                                    }, {
                                        header: 'Agency ID',
                                        field: 'agencyId',
                                        sort: sortByNumber,
                                        cell: bidder => typeof bidder.agencyId === 'number'
                                            ? <NavLink to={clientsLinks.agency(bidder.agencyId)}>{bidder.agencyId}</NavLink>
                                            : '—'
                                    }, {
                                        header: 'Status',
                                        field: 'status',
                                        sort: sortByString,
                                        responsive: true,
                                        cell: bidder => <TagsList values={bidderStatusTags(bidder)} size="small"/>
                                    }, {
                                        header: '',
                                        short: true,
                                        cell: bidder => ability.canEdit() ? <Button small
                                            leftIcon={<i className='fa fa-edit'/>}
                                            onClick={() => handleEditBidder(bidder.id)}
                                            disabled={pendingBidderId === bidder.id}
                                            rightIcon={pendingBidderId === bidder.id && <i className='fa fa-spinner fa-spin'/>}
                                            responsive
                                        >Edit</Button> : null
                                    }]}
                                />
                            </div>
                        </BreadcrumbsResourcePage>
                    </BidderFormCtx.Provider>
                );
            }}
        </form.Subscribe>
    </Breadcrumb>;
};

export const BidderGroupView = ({create}: {create?: boolean} = {}) => {
    if (create) {
        return <BidderGroupCreateView/>;
    }
    return <BidderGroupDetailsView/>;
};
